import { useMutateDeleteChat } from '@/hooks/mutation/useMutateDeleteChat';
import { useBaseChatHref } from '@heidi/agents/data';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/**
 * Deletes a chat and, on success, navigates to the chat home route.
 *
 * Lives directly under `src/hooks/` — NOT `src/hooks/mutation/` or
 * `src/hooks/query/` — because `ChatHeader.tsx` (inside the Chat quarantine,
 * `src/components/chat/CLAUDE.md`) cannot import `useMutateDeleteChat`
 * directly: `ChatHeader.tsx -> src/hooks/mutation/useMutateDeleteChat.ts` is
 * not an already-baselined outbound pair, and every composition root above
 * `ChatHeader` is itself a Chat glob, so a callback prop cannot invert the
 * dependency either (spec constraint C8). A neutral hook under `src/hooks/`
 * forms no pair in either direction — the same route `@/hooks/useSavedChats`
 * already uses for the same header.
 *
 * The mutation's own `onError` already surfaces a toast, so a failed delete
 * neither navigates nor needs to be handled again here.
 */
export function useDeleteChatWithHomeFallback(): {
  deleteChatWithHomeFallback: (chatId: string) => Promise<boolean>;
  isDeleting: boolean;
} {
  const router = useRouter();
  const homeHref = useBaseChatHref();
  const { mutateAsync, isPending } = useMutateDeleteChat();

  const deleteChatWithHomeFallback = useCallback(
    async (chatId: string): Promise<boolean> => {
      try {
        await mutateAsync(chatId);
      } catch {
        return false;
      }
      await router.push(homeHref);
      return true;
    },
    [homeHref, mutateAsync, router]
  );

  return { deleteChatWithHomeFallback, isDeleting: isPending };
}
